<script setup>
import EmptyState from '@/components/EmptyState';
import { ref, inject } from 'vue';
import { useStorageStore } from '@/stores/storage';
import { storeToRefs } from 'pinia';

const storageStore = useStorageStore();
const { search } = storageStore;
const { all } = storeToRefs(storageStore);

const params = ref({
    term: '',
    page: 1,
    sort_by: null,
    per_page: 10,
});

const searchStorages = () => {
    search({
        ...params.value,
    });
};

const modals = inject('modals');

const clearSearch = () => {
    params.value.term = '';
    searchStorages();
};

const tableUpdate = ({ page, sortBy, itemsPerPage }) => {
    Object.assign(params.value, { page, sort_by: sortBy, per_page: itemsPerPage });
    searchStorages();
};

const openModal = (modal, mode = 'Create', storage = null) => {
    modals.show(modal, {
        props: storage ? {
            storage: storage,
            mode: mode
        } : {
            mode: mode,
        }
    }).then(() => {
        searchStorages();
    }).catch(() => {
        // Modal cancelled
    });
};

const utilizationPercent = (item) => {
    if (!item.capacity) {
        return 0;
    }
    return Math.min(100, Math.round((item.stored / item.capacity) * 100));
};

const utilizationLabel = (item) => `${item.stored} / ${item.capacity}`;

const printStorageLabels = (item) => {
    window.open('/warehouse/' + item.hash + '/labels', '_blank');
};

const utilizationPercentLabel = (item) => `${utilizationPercent(item)}%`;
</script>

<template>
<MainContentLayout>
    <template #title>{{ $t('label.warehouse') }}</template>
    <template #actions>
        <StyledButton @click="openModal('StoragesModal')"><FontAwesomeIcon icon="plus" class="mr-1" />
            {{ $t('label.new_storage') }}
        </StyledButton>
    </template>
    <div class="rounded-xl border border-line bg-surface p-4 mb-4">
        <div class="grid lg:grid-cols-3 gap-x-3">
            <StyledSearch
                v-model="params.term"
                v-debounce:500ms="searchStorages"
                name="search"
                :label="$t('label.search')"
                :debounce-events="['input']"
                @click:clear="clearSearch" />
        </div>
    </div>
    <DataTable
        v-if="all"
        :data="all"
        :headers="[
            { title: $t('label.name'), key: 'name', value: item => item.name, sortable: false },
            { title: $t('label.type'), key: 'type', value: item => item.type === 'long_term' ? $t('label.long_term') : $t('label.short_term'), sortable: false },
            { title: $t('label.bins'), key: 'bins_count', value: item => item.bins_count, sortable: false },
            { title: $t('label.utilization'), key: 'utilization', value: item => `${item.stored} / ${item.capacity}`, sortable: false },
            { title: $t('label.status'), key: 'status', value: item => item.is_active ? $t('label.active') : $t('label.inactive'), sortable: false },
        ]"
        :actions="[
            { title: $t('title.edit'), icon: 'mdi-pencil-outline', action: (item) => openModal('StoragesModal', 'Edit', item) },
            { title: $t('label.generate_bins'), icon: 'mdi-grid', action: (item) => openModal('GenerateBinsModal', 'Create', item) },
            { title: $t('label.print_labels'), icon: 'mdi-printer-outline', isVisible: (item) => item.bins_count > 0, action: (item) => printStorageLabels(item) },
        ]"
        @table-update="tableUpdate">
        <template #[`item.utilization`]="{ item }">
            <div class="min-w-32">
                <div class="flex justify-between text-xs mb-1">
                    <span class="font-mono">{{ utilizationLabel(item) }}</span>
                    <span class="text-ink-light">{{ utilizationPercentLabel(item) }}</span>
                </div>
                <div class="h-2 rounded-full overflow-hidden bg-canvas">
                    <div
                        class="h-full rounded-full"
                        :class="utilizationPercent(item) >= 95 ? 'bg-warning' : 'bg-accent'"
                        :style="{ width: utilizationPercent(item) + '%' }" />
                </div>
            </div>
        </template>
        <template #[`item.status`]="{ item }">
            <StatusChip :color="item.is_active ? 'success' : 'neutral'">
                {{ item.is_active ? $t('label.active') : $t('label.inactive') }}
            </StatusChip>
        </template>
        <template #no-data>
            <EmptyState
                icon="mdi-warehouse"
                :title="$t('label.no_storages_found')"
                :hint="$t('label.no_storages_hint')">
                <StyledButton @click="openModal('StoragesModal')"><FontAwesomeIcon icon="plus" class="mr-1" />
                    {{ $t('label.new_storage') }}
                </StyledButton>
            </EmptyState>
        </template>
    </DataTable>
</MainContentLayout>
</template>
